<template>
  <base-dropdown :dropdownPosition="dropdownPosition" :useTeleport="useTeleport">
    <template #trigger="{
                mousedownHandler,
                blurHandler,
                focusHandler,
                keydownHandler,
                isShown,
                repositionDropdownHandler
              }"
    >
      <busy-button :id="name"
                   :is-loading="isBusy"
                   :variant="getPrimaryButtonVariant"
                   :class="primaryButtonClasses"
                   class="truncate"
                   :disabled="isDisabled"
                   @mousedown="!isDisabled && handlePrimaryButtonDropdown($event, mousedownHandler)"
                   @focus="!isDisabled && handlePrimaryButtonDropdown($event, focusHandler)"
                   @blur="handleTriggerBtnBlur($event, blurHandler)"
                   @keydown="!isDisabled && handleKeyPress($event, keydownHandler, mousedownHandler, isShown)"
                   @click.prevent="!isDisabled && $emit('trigger-action', selectedIndex)"
      >
        <div class="flex items-center">
          <span v-if="!hasSlotLabel" v-html="buttonProps.title || label"/>
          <component v-if="buttonProps.icon"
                     :is="buttonProps.icon.tag"
                     :icon="buttonProps.icon.identifier"
                     :src="buttonProps.icon.identifier"
                     size="1.3rem"
                     class="ml-1"
          />
          <slot name="label"/>
          <sort-desc-icon v-if="!isSplitButton && !hasSlotLabel"
                          class="absolute right-3 transform transition-transform duration-500"
                          :class="{ 'rotate-180': isShown }"
                          order="desc"
                          :is-active="true"
                          :color="arrowColor"
          />
        </div>
      </busy-button>
      <base-button v-if="isSplitButton"
                   :variant="getPrimaryButtonVariant"
                   class="rounded-r flex items-center"
                   data-cy="dropdown-button-toggle"
                   @mousedown="mousedownHandler"
                   @focus="focusHandler"
                   @blur="blurHandler"
                   @keydown="keydownHandler"
      >
        <sort-desc-icon class="transform transition-transform duration-500"
                        :class="{ 'rotate-180': isShown }"
                        order="desc"
                        :is-active="true"
        />
      </base-button>
    </template>

    <template #default="{ blurHandler, hide }">
      <base-button
        v-for="({ title, description, routerLink, icon, label }, i) in options"
        :key="title"
        :variant="dropdownButtonVariant"
        class="pr-4 pl-4 py-3 transition duration-150 ease-in-out"
        :class="[{ 'pr-6 pl-10': isSplitButton }, i === hoveredButtonIndex ? 'text-primary-focus' : 'text-gray']"
        role="menuitem"
        :router-link="routerLink"
        @blur="blurHandler"
        @click.prevent="selectOption(i, hide)"
        @mouseenter.native="hoveredButtonIndex = i"
        @mouseleave.native="hoveredButtonIndex = null"
      >
        <div>
          <div v-if="isSplitButton && selectedIndex === i" class="mr-1 absolute left-0 ml-3">
            <checkmark-icon/>
          </div>
          <div>
            <div class="flex mb-1 items-end" :class="{'flex-row-reverse': reverseOptionContent}">
              <div class="font-bold clamp" :title="title" v-html="label || title"/>
              <component v-if="icon"
                         :is="icon.tag"
                         :icon="icon.identifier"
                         :src="icon.identifier"
                         size="1.3rem"
                         class="ml-1"
              />
            </div>
            <span v-html="description"
                  :class="i === hoveredButtonIndex ? 'text-primary-focus' : 'text-gray-dark opacity-60'"
                  class="text-inherit"
            />
          </div>
        </div>
      </base-button>
    </template>
  </base-dropdown>
</template>

<script>
import BaseButton from '@/components/base-components/BaseButton'
import CheckmarkIcon from '@/assets/images/icons/CheckmarkIcon'
import LocalStorageUtils from '@/utils/local-storage.utils'
import LOCALE_STORAGE_KEYS from '@/utils/local-storage.keys'
import SortDescIcon from '@/components/common/SortableTableArrowsSort'
import BusyButton from './BusyButton'
import JavascriptIcon from '@/assets/images/icons/JavascriptIcon'
import ActionIcon from '@/assets/images/icons/ActionIcon'
import ChatGptTurboIcon from '@/assets/images/icons/ChatGptTurboIcon'
import clamp from 'ramda/src/clamp'
import { DROPDOWN_LOCATIONS } from '@/utils/dropdown-button.constants'
import BaseDropdown from '@/components/base-components/BaseDropdown.vue'

export default {
  name: 'DropdownButton',

  components: {
    BaseDropdown,
    BaseButton,
    SortDescIcon,
    CheckmarkIcon,
    BusyButton,
    JavascriptIcon,
    ActionIcon,
    ChatGptTurboIcon
  },

  props: {
    name: {
      type: String,
      required: true
    },
    dropdownPosition: {
      type: String,
      default: DROPDOWN_LOCATIONS.BELOW_LEFT
    },
    label: {
      type: String,
      default: ''
    },
    options: {
      type: Array,
      default: () => []
    },
    isSplitButton: {
      type: Boolean,
      default: false
    },
    isBusy: {
      type: Boolean,
      default: false
    },
    primaryButtonVariant: {
      type: String,
      default: 'primary'
    },
    dropdownButtonVariant: {
      type: String,
      default: 'dropdown'
    },
    onPrimaryButtonClick: {
      type: Function,
      default: () => {}
    },
    primaryButtonClasses: {
      type: String,
      default: ''
    },
    closeOnSelect: {
      type: Boolean,
      default: false
    },
    value: {
      type: String,
      default: ''
    },
    arrowColor: {
      type: String,
      default: 'white'
    },
    reverseOptionContent: {
      type: Boolean,
      default: false
    },
    useTeleport: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      CheckmarkIcon,
      selectedIndex: null,
      hoveredButtonIndex: null
    }
  },

  computed: {
    buttonProps() {
      return this.options[this.selectedIndex] || {}
    },

    getPrimaryButtonVariant() {
      if (this.isSplitButton) {
        return this.isBusy ? 'primary-grouped-disabled' : this.primaryButtonVariant
      }

      return this.isBusy ? 'primary-disabled' : this.primaryButtonVariant
    },

    hasSlotLabel() {
      return !!this.$slots?.label
    }
  },

  mounted() {
    if (this.isSplitButton) {
      const selectedIndex = LocalStorageUtils.getSegmentValue(LOCALE_STORAGE_KEYS.DROPDOWN_OPTIONS, this.name) || 0
      this.selectOption(selectedIndex)
    } else if (this.value) {
      // Highlight the current value without emitting select-button. That event is a user choice;
      // emitting it here rewrites the parent model as soon as the control mounts.
      this.selectedIndex = this.options.findIndex(({ value }) => value === this.value)
    }
  },

  methods: {
    selectOption(optionIndex, hide) {
      this.$emit('select-button', optionIndex)
      if (!this.isSplitButton && !this.closeOnSelect) return

      LocalStorageUtils.updateSegmentValue(LOCALE_STORAGE_KEYS.DROPDOWN_OPTIONS, this.name, optionIndex)
      this.selectedIndex = optionIndex
      if (hide) {
        hide()
        this.hoveredButtonIndex = null
      }
    },

    handlePrimaryButtonDropdown(e, dropDownHandler) {
      if (this.isSplitButton) return
      dropDownHandler(e)
    },

    handleTriggerBtnBlur(e, blurHandler) {
      if (e.relatedTarget?.role === 'menuitem') return
      this.handlePrimaryButtonDropdown(e, blurHandler)
    },

    handleKeyPress(e, keydownHandler, mousedownHandler, isDropdownVisible) {
      switch (e.code) {
        case 'ArrowDown':
          e.preventDefault()
          if (!isDropdownVisible) {
            mousedownHandler(e)
            return
          }

          this.selectedIndex = clamp(0, this.options.length - 1, this.hoveredButtonIndex === null ? 0 : this.hoveredButtonIndex + 1)
          this.hoveredButtonIndex = this.selectedIndex
          return
        case 'ArrowUp':
          e.preventDefault()
          this.selectedIndex = clamp(0, this.options.length - 1, this.hoveredButtonIndex - 1)
          this.hoveredButtonIndex = this.selectedIndex
          return
        case 'Space':
        case 'Enter':
          e.preventDefault()
          keydownHandler(e)
      }
    }
  }
}
</script>
